import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

const cyan = "#00f5ff";
const border = "rgba(0,245,255,0.15)";

const TYPES = [
  { key: "bug", label: "🐞 Bug", color: "#ff4466" },
  { key: "feature_request", label: "💡 Feature", color: "#ffaa00" },
  { key: "general", label: "💬 General", color: "#00f5ff" },
];

const inputStyle = {
  width: "100%",
  backgroundColor: "rgba(0,245,255,0.05)",
  border: `1px solid ${border}`,
  borderRadius: "6px",
  padding: "10px 14px",
  color: "white",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "'Segoe UI', sans-serif",
};
const labelStyle = {
  color: cyan,
  fontSize: "10px",
  letterSpacing: "2px",
  display: "block",
  marginBottom: "6px",
  fontWeight: "700",
};

// Mounted once at the AppContent level (see App.js) so it appears
// identically on every page — deliberately not embedded per-page, so it
// can never clash with or need updating alongside any individual page's
// layout.
// buttonStyle: overrides for the trigger button (Dashboard passes the
// phone-header size — at least 44px tall — on mobile).
export default function FeedbackButton({ token, buttonStyle }) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function reset() {
    setType("general");
    setSubject("");
    setMessage("");
    setError("");
    setSubmitted(false);
  }

  function closeModal() {
    setOpen(false);
    reset();
  }

  async function handleSubmit() {
    if (!subject.trim() || !message.trim()) {
      setError("Subject and message are both required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/feedback`,
        { type, subject, message },
        { headers: { Authorization: "Bearer " + token } },
      );
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit feedback — try again");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Inline trigger, sized to match the header's LOGOUT pill exactly
          (same fontSize/padding/borderRadius/letterSpacing) — this is
          rendered wherever the parent places it (Dashboard.js's top nav
          row), not fixed-position, so it can never float over page
          content the way the old bottom-left button did. */}
      <button
        onClick={() => setOpen(true)}
        style={{
          color: "rgba(0,245,255,0.7)",
          background: "none",
          border: `1px solid ${border}`,
          cursor: "pointer",
          fontSize: "11px",
          padding: "4px 12px",
          borderRadius: "4px",
          letterSpacing: "1px",
          whiteSpace: "nowrap",
          ...buttonStyle,
        }}
      >
        {tr("header.feedback")}
      </button>

      {/* Portaled to document.body — this button lives inside
          Dashboard.js's <nav>, which sets backdropFilter for its glass
          effect. backdrop-filter creates a new containing block for any
          position:fixed descendant (same as filter/transform would), so
          without the portal this overlay's inset:0 resolves against the
          nav's small box instead of the real viewport, clipping the
          modal near the top of the screen instead of centering it (see
          SmsSettingsButton.jsx, which hit the same bug). */}
      {open && createPortal(
        <div
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#020817",
              border: `1px solid ${border}`,
              borderRadius: "12px",
              padding: "32px",
              width: "100%",
              maxWidth: "460px",
              boxShadow: "0 0 60px rgba(0,245,255,0.1)",
            }}
          >
            {submitted ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>✓</div>
                <div
                  style={{
                    color: "#00cc66",
                    fontSize: "14px",
                    fontWeight: "700",
                    marginBottom: "8px",
                  }}
                >
                  Thanks — this has been sent to the RigIQ team.
                </div>
                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    marginBottom: "20px",
                  }}
                >
                  We read every submission. If it's a bug, we'll follow up if we
                  need more details.
                </p>
                <button
                  onClick={closeModal}
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    letterSpacing: "2px",
                    padding: "10px 24px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  CLOSE
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    color: cyan,
                    fontSize: "12px",
                    fontWeight: "700",
                    letterSpacing: "3px",
                    marginBottom: "24px",
                  }}
                >
                  SEND FEEDBACK
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>TYPE</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {TYPES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setType(t.key)}
                          style={{
                            flex: 1,
                            padding: "9px 6px",
                            background: type === t.key ? t.color + "22" : "rgba(0,245,255,0.05)",
                            color: type === t.key ? t.color : "rgba(255,255,255,0.5)",
                            border: `1px solid ${type === t.key ? t.color : border}`,
                            borderRadius: "6px",
                            fontSize: "10px",
                            fontWeight: "700",
                            letterSpacing: "1px",
                            cursor: "pointer",
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>SUBJECT</label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Short summary"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>MESSAGE</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={5}
                      placeholder="What happened, or what would help?"
                      style={{ ...inputStyle, resize: "vertical" }}
                    />
                  </div>
                </div>

                {error && (
                  <div style={{ color: "#ff4466", fontSize: "12px", marginTop: "12px" }}>
                    ⚠ {error}
                  </div>
                )}

                <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                  <button
                    onClick={closeModal}
                    style={{
                      flex: 1,
                      background: "none",
                      border: `1px solid ${border}`,
                      color: "rgba(0,245,255,0.5)",
                      padding: "10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "11px",
                      letterSpacing: "2px",
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{
                      flex: 2,
                      background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                      color: "#020817",
                      fontWeight: "700",
                      fontSize: "11px",
                      letterSpacing: "2px",
                      padding: "10px",
                      borderRadius: "6px",
                      border: "none",
                      cursor: submitting ? "not-allowed" : "pointer",
                      opacity: submitting ? 0.7 : 1,
                    }}
                  >
                    {submitting ? "SENDING..." : "SUBMIT"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
